import { GoogleGenAI, createUserContent } from '@google/genai';
import { config } from '../config';
import { parse } from 'best-effort-json-parser';

const genAI = new GoogleGenAI({ apiKey: config.geminiApiKey });

/**
 * 翻译配置选项接口
 * @typedef {Object} TranslationOptions
 * @property {string} targetLanguage - 目标语言，默认为 'zh-CN'（简体中文）
 * @property {string} tone - 翻译语气，可选：'formal'（正式）, 'casual'（随意）, 'friendly'（友好）
 * @property {boolean} keepOriginal - 是否在结果中保留原文
 */

/**
 * 翻译结果接口
 * @typedef {Object} TranslationResult
 * @property {string} originalText - 原始文本
 * @property {string} translatedText - 翻译后的文本
 * @property {string} detectedLanguage - 检测到的源语言
 * @property {Object} [context] - 额外的上下文信息（如果有）
 */

/**
 * 使用 Gemini 翻译文本
 * @param {string} text - 需要翻译的文本
 * @param {TranslationOptions} [options={}] - 翻译选项
 * @param {Function} [callback] - 回调函数
 * @returns {Promise<TranslationResult>} 翻译结果
 */
export async function translateText(text, options = {}, callback) {
  try {
    // 默认选项
    // console.log(text,options)
    const defaultOptions = {
      targetLanguage: 'zh-CN',
      tone: 'friendly',
      keepOriginal: true
    };

    // 合并选项
    const finalOptions = { ...defaultOptions, ...options };
    console.log(finalOptions);

    // 构建提示词
    const prompt = `
      将以下文本翻译成${finalOptions.targetLanguage === 'zh-CN' ? '中文' : finalOptions.targetLanguage}。
      ${text}
      以JSON格式返回结果，包含以下字段：
      - translatedText: 翻译后的文本
      - detectedLanguage: 检测到的源语言
      - context: 任何相关的文化或语言背景注释（如果有）
      只返回JSON，不要包含其他说明文字。
    `;

    // 调用 Gemini API
    const result = await genAI.models.generateContent({
      model: "gemini-2.0-flash",
      contents: createUserContent([prompt]),
      config: {
        responseMimeType: 'application/json',
        maxOutputTokens: 65533,
        temperature: 0.1,
      }
    });
    console.log(result)
    const responseText = result.text;
    
    // 解析返回的 JSON
    const translationData = parse(responseText);
    console.log(translationData)
    // 构建返回结果
    const finalResult = {
      originalText: finalOptions.keepOriginal ? text : undefined,
      ...translationData
    };

    // 如果有回调函数，调用它
    if (callback) {
      callback(finalResult);
    }

    return finalResult;
  } catch (error) {
    console.error('翻译服务错误:', error);
    throw new Error(`翻译失败: ${error.message}`);
  }
}

/**
 * 批量翻译多个文本
 * @param {string[]} texts - 需要翻译的文本数组
 * @param {TranslationOptions} [options={}] - 翻译选项
 * @param {Function} [callback] - 回调函数
 * @returns {Promise<TranslationResult[]>} 翻译结果数组
 */
export async function translateBatch(texts, options = {}, callback) {
  try {
    let results = [];
    for (const text of texts) {
      const result = await translateText(text, options, (partialResult) => {
        if (callback) {
          callback({
            ...partialResult,
            progress: {
              current: texts.indexOf(text) + 1,
              total: texts.length
            }
          });
        }
      });
      results.push(result);
    }
    return results;
  } catch (error) {
    console.error('批量翻译错误:', error);
    throw new Error(`批量翻译失败: ${error.message}`);
  }
}

/**
 * 自动检测语言并翻译
 * @param {string} text - 需要翻译的文本
 * @param {Function} [callback] - 回调函数
 * @returns {Promise<TranslationResult>} 翻译结果
 */
export async function autoTranslate(text, callback) {
  try {
    const prompt = `
      请检测以下文本的语言，并翻译成最合适的目标语言（如果是中文就翻译成英文，如果是其他语言就翻译成中文）。
      
      文本：
      ${text}
      
      请以JSON格式返回结果，包含以下字段：
      - detectedLanguage: 检测到的源语言
      - targetLanguage: 选择的目标语言
      - translatedText: 翻译后的文本
      - explanation: 为什么选择这个目标语言
      
      只返回JSON，不要包含其他说明文字。
    `;

    const result = await genAI.models.generateContent({
      model: "gemini-2.5-flash-preview-04-17",
      contents: createUserContent([prompt]),
      config: {
        responseMimeType: 'application/json',
        maxOutputTokens: 65533,
        temperature: 0.3,
      }
    });

    const response = result.response;
    const responseText = response.text();
    
    // 解析返回的 JSON
    const translationData = parse(responseText);
    
    // 构建返回结果
    const finalResult = {
      originalText: text,
      ...translationData
    };

    // 如果有回调函数，调用它
    if (callback) {
      callback(finalResult);
    }

    return finalResult;
  } catch (error) {
    console.error('自动翻译错误:', error);
    throw new Error(`自动翻译失败: ${error.message}`);
  }
}

// 导出服务
export const translationService = {
  translateText,
  translateBatch,
  autoTranslate
}; 